/* ============================================================
   Print / PDF trigger.
   ============================================================ */

import { printToPDF } from '../shared/pdf.js';
import { icon } from '../shared/icons.js';
import { toast } from '../shared/toast.js';

export function PrintButton({
  targetId,
  title,
  subtitle,
  footnote,
  /* Built on click, not on mount. The preamble stamps the date it was
     prepared, and a page left open overnight would otherwise export a
     document dated yesterday. */
  buildPreamble = null,
  hintText = 'Choose “Save as PDF” as the destination in the print dialog.'
}) {
  const wrap = document.createElement('div');
  wrap.className = 'no-print';
  wrap.style.marginTop = 'var(--ca-space-5)';

  /* One label, and no way for a caller to supply another.

     This used to take a `label` option. Five of the seventeen call sites
     used it, between them producing four names for one control: "Export
     as a board pack (PDF)", "Print this stack", "Print this structure"
     and "Export this profile as PDF", alongside twelve reading "Export
     as PDF". Same component, same action — open the print dialog, choose
     Save as PDF — described four ways, so a reader learns the label
     rather than the action and cannot tell whether "print" and "export"
     differ here. They do not.

     Removing the option rather than correcting the five, because
     correcting them fixes today and the option is what produced the
     divergence. Where a tool's export really is a board pack rather than
     the page, that belongs in the heading above the button or in the
     hint below it — both of which are still per-caller. */
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'btn btn-primary btn-block';
  /* A hook the harnesses can hold that is not the label. Two smoke tests
     found this control with :has-text("Export as a board pack") and broke
     on the rename — reasonably, since prose was the only handle offered. */
  button.dataset.export = 'pdf';
  button.innerHTML = `${icon('download', 16)}<span>Export as PDF</span>`;

  button.addEventListener('click', async () => {
    button.disabled = true;
    const original = button.innerHTML;
    button.innerHTML = '<span class="spinner" style="width:16px;height:16px;"></span><span>Preparing…</span>';

    /* try/finally, because the restore is not optional.

       printToPDF guards itself and always returns, so for a long time
       nothing on this path could throw and the naked restore was safe.
       Then buildPreamble arrived in front of it — a caller-supplied
       function that composes a document out of a result object. If it
       throws, the old code never reached the last two lines: the button
       stayed disabled, spinning, for the life of the page, and the only
       way out was a reload. A failed export is a bad moment; an export
       button that never comes back is a broken page. */
    try {
      const preamble = typeof buildPreamble === 'function' ? buildPreamble() : null;
      const ok = await printToPDF(targetId, { title, subtitle, footnote, preamble });
      if (!ok) toast('Could not open the print dialog on this device.', { tone: 'error' });
    } catch (error) {
      console.error('Export failed:', error);
      toast('Could not build the export. The results on screen are unaffected.', { tone: 'error' });
    } finally {
      button.disabled = false;
      button.innerHTML = original;
    }
  });

  const hint = document.createElement('p');
  hint.style.cssText =
    'font-size: var(--ca-text-xs); color: var(--ca-text-quaternary); text-align: center; margin-top: var(--ca-space-2);';
  hint.textContent = hintText;

  wrap.append(button, hint);
  return wrap;
}
